import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { ReportService } from '../../service/data/report.service';
import { of, Subscription } from 'rxjs';
import { CompanyService } from '../../../rent/service/data/company.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { switchMap } from 'rxjs/operators';
import { LocalDate } from '../../../core/model/local-date';
import { canSubmitReportForm } from '../../utility/report-form-validation';

@Component({
    selector: 'app-membership-list',
    templateUrl: './membership-list.component.html',
    styleUrls: ['./membership-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MembershipListComponent implements OnDestroy, OnInit {
  constructor(
    private _cdf: ColumnDefinitionFactory,
    private _dataService: ReportService,
    companyService: CompanyService,
    private _fb: FormBuilder,
    private _nativeWindow: NativeWindowService,
    private _fcf: FormControlExFactory
  ) {
    this.companyService = companyService;
  }

  private _exportSub: Subscription;
  private _sub = new Subscription();
  public companyService: CompanyService;
  public isExportDisabled = true;

  ngOnInit() {
    this.createColumnDefinitions();
    this.form = this._fb.group({
      companyId: this._fcf.createMandatory('COMMON.COMPANY', null),
      reportDate: this._fcf.createMandatory('REPORT.REPORT_DATE', LocalDate.today()),
    });
    this._sub.add(
      this.form.controls['companyId'].valueChanges
        .pipe(
          switchMap(val => {
            this.isExportDisabled = !val;
            const reportDate = this.form.controls['reportDate'];
            if (!val || reportDate.invalid) {
              if (val) reportDate.markAsTouched();
              return of([]);
            }

            return this._dataService.getMembershipList(val, reportDate.value);
          })
        )
        .subscribe(list => {
          this.dataContext = list;
        })
    );
    this._sub.add(
      this.form.controls['reportDate'].valueChanges
        .pipe(
          switchMap(val => {
            if (this.isExportDisabled || this.form.controls['reportDate'].invalid) {
              if (!this.isExportDisabled) this.form.controls['reportDate'].markAsTouched();
              return [];
            }

            return this._dataService.getMembershipList(this.form.controls['companyId'].value, val);
          })
        )
        .subscribe(list => {
          this.dataContext = list;
        })
    );
  }

  exportExcel(): void {
    if (!canSubmitReportForm(this.form)) return;

    if (this._exportSub) {
      this._exportSub.unsubscribe();
    }
    this._exportSub = this._dataService
      .getMembershipListExcel(this.form.controls['companyId'].value, this.form.controls['reportDate'].value)
      .subscribe(file => {
        this._nativeWindow.save(file);
      });
  }

  onPrintPages() {
    if (!canSubmitReportForm(this.form)) return;
    window.print();
  }

  private createColumnDefinitions() {
    this.columnDefinitions = [
      this._cdf.create('TENANT.NAME', 'contactName', ColumnVisibility.mandatory),
      this._cdf.create('TENANT.CIVIC_NUMBER', 'contactIdString', ColumnVisibility.optional),
      this._cdf.createDate('COOPERATIVE_HOUSING.MEMBER_FROM', 'memberFrom', ColumnVisibility.default),
      this._cdf.createDate('COOPERATIVE_HOUSING.MEMBER_TO', 'memberTo', ColumnVisibility.default),
      this._cdf.createDate('COOPERATIVE_HOUSING.MEMBER_APPROVAL_DATE', 'approvalDate', ColumnVisibility.default),
      this._cdf.create('COMMON.EMAIL', 'contactEmail', ColumnVisibility.optional),
      this._cdf.create('COMMON.PHONE_NUMBER', 'contactPhone', ColumnVisibility.optional),
      this._cdf.create('COMMON.ADDRESS', 'contactAddress', ColumnVisibility.optional),
      this._cdf.createDecimal('COOPERATIVE_HOUSING.OWNED_SHARE', 'ownedPercent', ColumnVisibility.default),
      this._cdf.createDecimal('COOPERATIVE_HOUSING.OWNED_SHARE_OF_COMPANY', 'contactCompanyShare', {
        visibility: ColumnVisibility.default,
        totalSum: true,
      }),
      this._cdf.createDecimal('COOPERATIVE_HOUSING.OWNED_PERCENT_OF_COMPANY', 'contactCompanyPercent', {
        visibility: ColumnVisibility.default,
        totalSum: true,
      }),
      this._cdf.create('RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId', ColumnVisibility.default),
      this._cdf.create('RENTAL_OBJECT_TYPE_LIST.NAME_COLUMN', 'rentalObjectType', ColumnVisibility.optional),
      this._cdf.createDecimal('COOPERATIVE_HOUSING.SHARE', 'objectCompanyShare', { visibility: ColumnVisibility.default, totalSum: true }),
      this._cdf.createDecimal('COOPERATIVE_HOUSING.SHARE_PERCENT', 'objectCompanyPercent', {
        visibility: ColumnVisibility.default,
        totalSum: true,
      }),
    ];
  }

  ngOnDestroy(): void {
    this._sub?.unsubscribe();
  }

  columnDefinitions: ColumnDefinition[];
  dataContext: any[];
  form: FormGroup;
}
